import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import IntakeFormTypeForm from '../components/IntakeFormTypeForm';
import FieldMappingManager from '../components/FieldMappingManager';
import intakeFormTypeService, {
  IntakeFormType,
} from '../services/intakeFormType.service';

const IntakeFormTypeList: React.FC = () => {
  const [intakeFormTypes, setIntakeFormTypes] = useState<IntakeFormType[]>([]);
  const [selected, setSelected] = useState<IntakeFormType | null>(null);
  const [open, setOpen] = useState(false);
  const [openFieldMappings, setOpenFieldMappings] = useState(false);
  const [selectedForFieldMappings, setSelectedForFieldMappings] =
    useState<IntakeFormType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIntakeFormTypes = async () => {
    try {
      setLoading(true);
      const data = await intakeFormTypeService.getAll();
      setIntakeFormTypes(data);
      setError(null);
    } catch (err) {
      setError('Failed to load intake form types');
      console.error('Error loading intake form types:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntakeFormTypes();
  }, []);

  const handleCreate = () => {
    setSelected(null);
    setOpen(true);
  };

  const handleEdit = (intakeFormType: IntakeFormType) => {
    setSelected(intakeFormType);
    setOpen(true);
  };

  const handleFieldMappings = (intakeFormType: IntakeFormType) => {
    setSelectedForFieldMappings(intakeFormType);
    setOpenFieldMappings(true);
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm('Are you sure you want to delete this intake form type?')
    ) {
      try {
        await intakeFormTypeService.delete(id);
        fetchIntakeFormTypes();
      } catch (err) {
        setError('Failed to delete intake form type');
        console.error('Error deleting intake form type:', err);
      }
    }
  };

  const handleDownloadTemplate = async (id: number, filename: string) => {
    try {
      const blob = await intakeFormTypeService.downloadTemplate(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template');
      console.error('Error downloading template:', err);
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      setLoading(true);
      console.log('List handleSubmit - selected:', selected);
      console.log('List handleSubmit - data:', data);

      if (selected) {
        console.log('Updating intake form type with ID:', selected.id);
        await intakeFormTypeService.update(selected.id, data);
      } else {
        console.log('Creating new intake form type');
        await intakeFormTypeService.create(data);
      }
      setOpen(false);
      fetchIntakeFormTypes();
    } catch (err) {
      setError('Failed to save intake form type');
      console.error('Error saving intake form type:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h4' gutterBottom>
        Intake Form Types
      </Typography>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Button
        variant='contained'
        color='primary'
        onClick={handleCreate}
        sx={{ mb: 2 }}
      >
        Add Intake Form Type
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Short Code</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Service Types</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {intakeFormTypes.map(intakeFormType => (
              <TableRow key={intakeFormType.id}>
                <TableCell>{intakeFormType.name}</TableCell>
                <TableCell>
                  <Chip label={intakeFormType.short_code} size='small'   onClick={() => false}/>
                </TableCell>
                <TableCell>{intakeFormType.description}</TableCell>
                <TableCell>
                  <Chip
                    label={intakeFormType.is_active ? 'Active' : 'Inactive'}
                    color={intakeFormType.is_active ? 'success' : 'default'}
                    size='small'
                    onClick={() => false}
                  />
                </TableCell>
                <TableCell>{intakeFormType.sort_order}</TableCell>
                <TableCell>
                  {intakeFormType.template_filename ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={intakeFormType.template_filename}
                        size='small'
                        icon={<DownloadIcon />}
                        onClick={() =>
                          handleDownloadTemplate(
                            intakeFormType.id,
                            intakeFormType.template_filename!
                          )
                        }
                      />
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      No template
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {intakeFormType.service_types?.map(serviceType => (
                    <Chip
                      key={serviceType.id}
                      label={`${serviceType.name}${serviceType.pivot.is_required ? ' (Required)' : ''}`}
                      size='small'
                      sx={{ mr: 0.5, mb: 0.5 }}
                      onClick={() => false}
                    />
                  )) || 'None'}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(intakeFormType)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleFieldMappings(intakeFormType)}
                  >
                    <SettingsIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(intakeFormType.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {selected ? 'Edit Intake Form Type' : 'Add Intake Form Type'}
        </DialogTitle>
        <DialogContent>
          <IntakeFormTypeForm
            initialValues={selected || undefined}
            onSubmit={handleSubmit}
            loading={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openFieldMappings}
        onClose={() => setOpenFieldMappings(false)}
        fullScreen
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 1.5,
            px: 3,
          }}
        >
          <Box>
            <Typography variant='h6' component='span'>Field Mappings</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ ml: 1 }} component='span'>
              — {selectedForFieldMappings?.name}
            </Typography>
          </Box>
          <Button variant='outlined' onClick={() => setOpenFieldMappings(false)}>
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {selectedForFieldMappings && (
            <FieldMappingManager
              intakeFormTypeId={selectedForFieldMappings.id}
              intakeFormTypeName={selectedForFieldMappings.name}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default IntakeFormTypeList;
